import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { Alert, Empty, Kpi, KpiGrid, Muted, PageHeader, Section } from '@/components/ui'
import { syncBitrixCadastro, type SyncBitrixResult } from './api'
import { ImportPipelineNav } from './ImportPipelineNav'

export function ImportarBitrixPage() {
  const { canEdit } = useAuth()
  const editable = canEdit('colaboradores')
  const qc = useQueryClient()

  const [criarFaltantes, setCriarFaltantes] = useState(false)
  const [forceFotos, setForceFotos] = useState(false)
  const [msg, setMsg] = useState('')
  const [erro, setErro] = useState(false)
  const [pending, setPending] = useState(false)
  const [resultado, setResultado] = useState<SyncBitrixResult | null>(null)

  async function executar(opts: { somenteFotos?: boolean } = {}) {
    const somenteFotos = Boolean(opts.somenteFotos)
    setPending(true)
    setErro(false)
    setMsg(
      somenteFotos
        ? 'Corrigindo fotos só por id_bitrix (sem rematch por nome)…'
        : 'Sincronizando Bitrix (e-mail, foto, id_bitrix)…',
    )
    setResultado(null)
    try {
      const res = await syncBitrixCadastro({
        criarFaltantes: somenteFotos ? false : criarFaltantes,
        forceFotos: somenteFotos ? true : forceFotos,
        somenteFotos,
        onProgress: (st) => {
          if (st.status === 'queued' || st.status === 'running') {
            setMsg(
              st.mensagem
              || (somenteFotos
                ? 'Corrigindo fotos em segundo plano…'
                : 'Sincronizando Bitrix em segundo plano (pode levar vários minutos com fotos)…'),
            )
          }
        },
      })
      setResultado(res)
      const puladas = res.fotos_puladas_match_fraco ?? 0
      setMsg(
        res.sucesso === false
          ? res.mensagem || 'Falha no sync Bitrix.'
          : somenteFotos
            ? `Fotos corrigidas. ${res.fotos ?? 0} rebaixadas · ${res.sem_match ?? 0} sem id_bitrix.`
            : `Bitrix concluído. ${res.atualizados ?? 0} atualizados · ${res.criados ?? 0} criados · ${res.desligados ?? 0} desligados · ${res.reativados ?? 0} reativados · ${res.fotos ?? 0} fotos · ${res.sem_match ?? 0} sem match${puladas ? ` · ${puladas} foto(s) pulada(s) (match fraco)` : ''}.`,
      )
      setErro(res.sucesso === false)
      if (res.sucesso !== false) {
        await qc.invalidateQueries({ queryKey: ['rh', 'colaboradores'] })
        await qc.invalidateQueries({ queryKey: ['rh', 'kpis'] })
      }
    } catch (err) {
      setResultado(null)
      setErro(true)
      setMsg(err instanceof ApiError ? err.message : 'Falha na sincronização Bitrix.')
    } finally {
      setPending(false)
    }
  }

  if (!editable) {
    return (
      <section className="hi-page">
        <PageHeader title="Importar Bitrix" subtitle="Acesso restrito a RH/Admin." />
        <Alert tone="danger">Sem permissão para sincronizar Bitrix.</Alert>
      </section>
    )
  }

  return (
    <section className="hi-page hi-import-colab-page">
      <PageHeader
        title="Importar Bitrix"
        subtitle="E-mail profissional, telefone, foto e situação: ACTIVE no Bitrix = ativo; desativado no Bitrix = desligado."
        actions={
          <Link className="btn btn-secondary" to="/app/rh">
            Voltar à lista
          </Link>
        }
      />

      <ImportPipelineNav active="bitrix" />

      {msg ? <Alert tone={erro ? 'danger' : 'info'}>{msg}</Alert> : null}

      <Section title="Sincronizar">
        <form
          className="hi-filters hi-import-form"
          onSubmit={(e) => {
            e.preventDefault()
            void executar()
          }}
        >
          <label className="hi-check">
            <input
              type="checkbox"
              checked={criarFaltantes}
              disabled={pending}
              onChange={(e) => setCriarFaltantes(e.target.checked)}
            />
            Criar quem não existir (só se ACTIVE no Bitrix)
          </label>
          <label className="hi-check">
            <input
              type="checkbox"
              checked={forceFotos}
              disabled={pending}
              onChange={(e) => setForceFotos(e.target.checked)}
            />
            Forçar redownload de fotos (só em match confiável)
          </label>
          <button type="submit" className="btn btn-primary" disabled={pending}>
            {pending ? 'Sincronizando…' : 'Sincronizar Bitrix'}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={pending}
            onClick={() => {
              if (
                confirm(
                  'Rebaixar fotos apenas quando id_bitrix + nome/e-mail forem compatíveis? Vínculos errados (ex.: e-mail de outra pessoa) serão removidos.',
                )
              ) {
                void executar({ somenteFotos: true })
              }
            }}
          >
            Corrigir fotos / vínculos errados
          </button>
        </form>
        <Muted>
          Situação vem do Bitrix (ACTIVE). Desativar no Bitrix marca desligado aqui; reativar
          volta para ativo. Match exige nome/e-mail compatível com a folha. Depois use{' '}
          <Link to="/app/rh/importar/duplicados">Duplicados</Link> se ainda houver cadastros
          duplicados.
        </Muted>
      </Section>

      {resultado && resultado.sucesso !== false ? (
        <>
          <KpiGrid>
            <Kpi label="Ativos Bitrix" value={String(resultado.ativos_bitrix ?? 0)} />
            <Kpi label="Inativos Bitrix" value={String(resultado.inativos_bitrix ?? 0)} />
            <Kpi label="Atualizados" value={String(resultado.atualizados ?? 0)} />
            <Kpi label="Criados" value={String(resultado.criados ?? 0)} />
            <Kpi label="Desligados" value={String(resultado.desligados ?? 0)} />
            <Kpi label="Reativados" value={String(resultado.reativados ?? 0)} />
            <Kpi label="Sem match" value={String(resultado.sem_match ?? 0)} />
            <Kpi label="Fotos" value={String(resultado.fotos ?? 0)} />
            <Kpi
              label="Fotos puladas"
              value={String(resultado.fotos_puladas_match_fraco ?? 0)}
            />
            <Kpi label="Erros" value={String(resultado.erros ?? 0)} />
          </KpiGrid>

          <Section title="Detalhes" flush>
            {(resultado.detalhes_erro?.length ?? 0) === 0 ? (
              <Empty>Sem erros nesta rodada.</Empty>
            ) : (
              <div className="hi-import-detalhes">
                <Muted>Erros (amostra)</Muted>
                <ul>
                  {resultado.detalhes_erro!.map((d, i) => (
                    <li key={`bx-${i}`}>
                      id {d.id}: {d.erro}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Section>
        </>
      ) : null}
    </section>
  )
}
